import * as React from "react";
import { useParams } from "react-router-dom";
import UserPayroll from "../index";
import { useEffect, useState } from "react";
import UserProfile from "../..";
import { statusToClass } from '../../../../utils/statusStyles';
import { connect } from "react-redux";
import {
  AddUserAdjustment,
  DeleteUserAdjustment,
  GetUserAdjustmentsPerMonth,
  UpdateUserAdjustment,
} from "../../../../api/Payroll/adjustment";
import YearMonthPicker from "../../../../components/yearMonthPicker";
import { UserAdjustmentInput, UserAdjustmentView, UserAdjustmentType, UserAdjustmentStatus } from "../../../../models/payroll";
import { GetCompanyRates } from "../../../../api/General/currency";
import { getCompanyRules } from "../../../../api/General/Company";
import { CompanyCurrencyRate, Currency } from "../../../../models/general";
import { useTranslation } from "react-i18next";
import { formatDate, formatNumber } from "../../../../utils";
import CurrencyDropdown from "../../../../components/currencyDropdown";
import { AddAdjustmentDialog, EditAdjustmentDialog, DeleteAdjustmentDialog } from "./AdjustmentDialogs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import AppIcon from "../../../../components/AppIcon";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import * as Tooltip from "@radix-ui/react-tooltip";

type Props = Readonly<{
  component?: React.ComponentType<any> | undefined;
  role?: any;
  company?: any;
  userId?: any;
  CompanyCurrencyRate?: any;
}>;

const Adjustments = (props: Props) => {
  const { t } = useTranslation('common');
  const userRole = props.role;
  const { id } = useParams();
  const { toast, showToast, dismissToast } = useToast();

  const [addAdjustmentDialogOpen, setAddAdjustmentDialogOpen] = useState(false);
  const [editAdjustmentDialogOpen, setEditAdjustmentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString())
  const [month, setMonth] = useState((new Date().getMonth() + 1).toLocaleString('en-GB', { minimumIntegerDigits: 2 }))
  const [from, setFrom] = useState(new Date())
  const [to, setTo] = useState(new Date())
  const [adjustments, setAdjustments] = useState<UserAdjustmentView[]>([])
  const [userAdjustmentType, setUserAdjustmentType] = useState<UserAdjustmentType>(UserAdjustmentType.Addition)
  const [amount, setAmount] = useState<any>(0)
  const [remarks, setRemarks] = useState("")
  const [effectiveDate, setEffectiveDate] = useState<Date>(new Date())
  const [editId, setEditId] = useState(0)
  const [editInput, setEditInput] = useState<UserAdjustmentInput>({
    id: 0,
    userAdjustmentType: UserAdjustmentType.Addition,
    amount: 0,
    remarks: "",
    effectiveDate: new Date(),
    userId: id ?? ""
  });
  const [deleteId, setDeleteId] = useState<any>(0)
  const [Loading, setLoading] = useState(false)
  const [errorVisible, setErrorVisible] = useState(false)
  const [CompanyCurrencyRateRates, setCompanyCurrencyRateRates] = useState<CompanyCurrencyRate[]>([]);
  const [companyBaseCurrency, setCompanyBaseCurrency] = useState<Currency | null>();
  const [currency, setCurrency] = useState<any>(0);

  useEffect(() => {
    fetchData(id, year, month, props.CompanyCurrencyRate?.id)
  }, [id, year, month]);


  const fetchData = async (id: any, year: any, month: any, currencyId: any) => {
    setLoading(true)
    try {
      const userId = id ?? ""
      const adjustments = await GetUserAdjustmentsPerMonth(userId, `${year}-${month.toString().padStart(2, "0")}-01`, currencyId)
      if (adjustments.errors?.length === 0) setAdjustments(adjustments.payload)

      setLoading(false)

      setAmount(0)
      setUserAdjustmentType(UserAdjustmentType.Addition)
      setEffectiveDate(new Date())
      setErrorVisible(false)

      const CompanyCurrencyRateRates = await GetCompanyRates()
      const rates: CompanyCurrencyRate[] = []

      const rules = await getCompanyRules(props.company?.id)
      if (rules.errors?.length > 0 || !rules.payload?.currency) return;
      const baseCurr: CompanyCurrencyRate = {
        id: 0,
        baseCurrency: rules.payload.currency,
        baseCurrencyId: rules.payload.currency.id,
        foreignCurrency: rules.payload.currency,
        foreignCurrencyId: 0,
        company: props.company,
        companyId: props.company?.id,
        rate: 0,
        createdOn: new Date(),
        isArchived: false
      }
      rates.push(baseCurr)
      if (CompanyCurrencyRateRates.errors?.length === 0) {
        const rates_: CompanyCurrencyRate[] = CompanyCurrencyRateRates.payload
        rates.push(...rates_)
      }
      setCompanyBaseCurrency(rules.payload.currency);
      setCompanyCurrencyRateRates(rates);
    } catch {
      showToast(t('Failed to load adjustments'), 'error');
    }
  }

  const handleYearMonthChange = (date: Date) => {
    date.setHours(date.getHours() - (date.getTimezoneOffset() / 60))
    setYear(date.getFullYear().toString())
    setMonth((date.getMonth() + 1).toLocaleString('en-GB', { minimumIntegerDigits: 2 }))
    fetchData(id, year, (date.getMonth() + 1).toString(), 0)

    const fromDate = new Date(date)
    fromDate.setDate(fromDate.getDate() + 6)
    const toDate = new Date(fromDate)
    toDate.setDate(toDate.getDate() + 6)
    setFrom(fromDate)
    setTo(toDate)
  }

  const fromMonthDefault = React.useMemo(() => new Date(new Date().getFullYear() - 8, 0, 1), []);
  const toMonthDefault = React.useMemo(() => new Date(new Date().getFullYear() + 1, 11, 31), []);

  const handleOpenAdjustment = () => setAddAdjustmentDialogOpen(true);

  const submitAddAdjustment = async (input: Omit<UserAdjustmentInput, 'id'>) => {
    setLoading(true);
    try {
      const payload: UserAdjustmentInput = { id: 0, ...input } as UserAdjustmentInput;
      await AddUserAdjustment(payload);
      setAddAdjustmentDialogOpen(false);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setErrorVisible(false);
    } catch (error: any) {
      console.log(error);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => { if (err.description) showToast(err.description, 'error') });
      }
    } finally { setLoading(false); }
  };

  const handleOpenEdit = () => setEditAdjustmentDialogOpen(true);

  const submitEditAdjustment = async (input: UserAdjustmentInput) => {
    setLoading(true);
    try {
      const payload: UserAdjustmentInput = { ...input, userId: id ?? "", status: input.status };
      await UpdateUserAdjustment(payload);
      setEditAdjustmentDialogOpen(false);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
    } catch (error: any) {
      console.log(error);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => { if (err.description) showToast(err.description, 'error') });
      }
    } finally { setLoading(false); }
  };

  const handleOpenDelete = () => setDeleteDialogOpen(true);

  const deleteAdjustment = async () => {
    setLoading(true);
    try {
      await DeleteUserAdjustment(deleteId);
      setDeleteDialogOpen(false);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
    } catch (error: any) {
      console.log(error);
      await fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => { if (err.description) showToast(err.description, 'error') });
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
          <UserPayroll>
            <div className="flex-col gap-6">
              <div className="flex-between flex-wrap">
                <YearMonthPicker date={from} onChange={handleYearMonthChange} fromMonth={fromMonthDefault} toMonth={toMonthDefault} isRtl={false} />
                <CurrencyDropdown
                  value={currency}
                  options={CompanyCurrencyRateRates}
                  onOptionChange={(option: any) => { fetchData(id, year, month, option.foreignCurrencyId); setCurrency(option); }}
                />
                <div className="flex-grow" />
                {userRole === 'Hr' && (
                  <Button variant="default" onClick={handleOpenAdjustment} iconName="Plus">
                    {t('NEW ADJUSTMENT')}
                  </Button>
                )}
              </div>

              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {Loading && (
                  <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '100%' }} />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('Type')}</TableHead>
                        <TableHead>{t('Amount')}</TableHead>
                        <TableHead>{t('Remarks')}</TableHead>
                        <TableHead>{t('Effective Date')}</TableHead>
                        <TableHead>{t('Status')}</TableHead>
                        <TableHead className="text-end">{t('Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!Loading && adjustments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <p className="text-sm text-muted-foreground">{t('No adjustments found for this period.')}</p>
                          </TableCell>
                        </TableRow>
                      )}
                      {!Loading && adjustments.map((row: UserAdjustmentView) => {
                        const locked = row.status === UserAdjustmentStatus.Paid || row.status === UserAdjustmentStatus.PendingPayment;
                        return (
                          <TableRow key={row.id}>
                            <TableCell>{t(row.userAdjustmentType)}</TableCell>
                            <TableCell>{formatNumber(row.amount)}</TableCell>
                            <TableCell>
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <span className="truncate max-w-[240px] inline-block align-top">{row.remarks || '-'}</span>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {row.remarks || ''}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                            </TableCell>
                            <TableCell>{formatDate(new Date(row.effectiveDate))}</TableCell>
                            <TableCell>
                              <div className={statusToClass(row.status)}>{t(row.status)}</div>
                            </TableCell>
                            <TableCell className="text-end">
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <span>
                                      <Button variant="ghost" size="icon" disabled={locked} onClick={() => { setDeleteId(row.id); handleOpenDelete(); }}>
                                        <AppIcon name="Trash2" size={16} />
                                      </Button>
                                    </span>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {locked ? t('Cannot modify in current status') : t('Delete')}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <span>
                                      <Button variant="ghost" size="icon" disabled={locked} onClick={() => {
                                        setEditId(row.id);
                                        setEditInput({ id: row.id, userId: id, userAdjustmentType: row.userAdjustmentType, amount: row.amount, effectiveDate: row.effectiveDate, remarks: row.remarks });
                                        handleOpenEdit();
                                      }}>
                                        <AppIcon name="Pencil" size={16} />
                                      </Button>
                                    </span>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {t('Edit')}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {Loading && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-6">
                            <svg className="animate-spin h-8 w-8 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            <ToastRenderer toast={toast} onDismiss={dismissToast} />
          </UserPayroll>
      </UserProfile>
      <AddAdjustmentDialog
        open={addAdjustmentDialogOpen}
        loading={Loading}
        onClose={() => setAddAdjustmentDialogOpen(false)}
        onSubmit={submitAddAdjustment}
        userId={id ?? ''}
      />
      <EditAdjustmentDialog
        open={editAdjustmentDialogOpen}
        loading={Loading}
        onClose={() => setEditAdjustmentDialogOpen(false)}
        onSubmit={submitEditAdjustment}
        value={editInput}
      />
      <DeleteAdjustmentDialog
        open={deleteDialogOpen}
        loading={Loading}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={deleteAdjustment}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    company: state.user.company,
    CompanyCurrencyRate: state.user.CompanyCurrencyRate,
  };
};
export default connect(mapStateToProps)(Adjustments) as any;
